import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import type { FeaturePack } from '../types/api';

interface UseFeaturePacksOptions {
  autoRefresh?: boolean;
  refreshInterval?: number;
}

interface UseFeaturePacksReturn {
  featurePacks: FeaturePack[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  createFeaturePack: (featurePack: FeaturePack) => Promise<FeaturePack>;
  deleteFeaturePack: (name: string, version?: string) => Promise<void>;
}

export function useFeaturePacks(options: UseFeaturePacksOptions = {}): UseFeaturePacksReturn {
  const { autoRefresh = false, refreshInterval = 60000 } = options;

  const [featurePacks, setFeaturePacks] = useState<FeaturePack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchFeaturePacks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getFeaturePacks();
      setFeaturePacks(data);
    } catch (err) {
      setError(err as Error);
      console.error('Failed to fetch feature packs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchFeaturePacks();
  }, [fetchFeaturePacks]);

  const createFeaturePack = useCallback(async (featurePack: FeaturePack): Promise<FeaturePack> => {
    try {
      const created = await apiService.createFeaturePack(featurePack);
      await fetchFeaturePacks();
      return created;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [fetchFeaturePacks]);

  const deleteFeaturePack = useCallback(async (name: string, version?: string) => {
    try {
      await apiService.deleteFeaturePack(name, version);
      await fetchFeaturePacks();
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [fetchFeaturePacks]);

  useEffect(() => {
    fetchFeaturePacks();
  }, [fetchFeaturePacks]);

  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      const interval = setInterval(fetchFeaturePacks, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, fetchFeaturePacks]);

  return {
    featurePacks,
    loading,
    error,
    refresh,
    createFeaturePack,
    deleteFeaturePack,
  };
}
